import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  LANGUAGE_DEFAULT_OPTION,
  LANGUAGE_OPTIONS,
  DOMAIN,
  REGION_SG,
  STATUS_CODE_RESPONSE_OK
} from "../../utils/constant";
import { fetchCodeDictionaries, fetchLanguageOptions } from "../../redux/managePackageSlice";
import {
  fetchIndustryProducts,
  updateIndustryProducts,
  clearUpdateStatus
} from "../../redux/packageIndustrySlice";
import CustomerStories from './CustomerStories';
import TrustedPartners from './TrustedPartners';
import FAQ from './Faq';
import { saveIndustryContent } from '../../redux/homePageSlice';
import { decodedJWTPayload } from '../../utils/commonUtil';

function ManageIndustry() {
  const dispatch = useDispatch();
  const [language, setLanguage] = useState(LANGUAGE_DEFAULT_OPTION);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [activeTab, setActiveTab] = useState('industry');

  const [permission, setPermission] = useState('RWX');

  const { codeDictionaries, dictionaryLoading, languageOptions: languageOptionsFromRedux, languageOptionsLoading } = useSelector((state) => state.managePackage);
  const { industryProducts, productsLoading, productsError, updateLoading, updateError, updateSuccess } = useSelector((state) => state.packageIndustry);

  const [saving, setSaving] = useState(false);

  const [availableProducts, setAvailableProducts] = useState([]);

  const customerStoriesRef = useRef();
  const trustedPartnersRef = useRef();
  const faqRef = useRef();

  const getLanguageOptions = () => {
    if (!languageOptionsFromRedux?.resdata?.data) return [];

    const options = [];
    const languageData = languageOptionsFromRedux.resdata.data;

    for (const langCode in languageData) {
      if (Array.isArray(languageData[langCode])) {
        languageData[langCode].forEach(item => {
          if (item.type === 'LANGUAGE' && item.codes?.length) {
            item.codes.forEach(codeItem => {
              options.push({
                value: codeItem.code,
                label: codeItem.value
              });
            });
          }
        });
      }
    }

    return options;
  };

  const languageOptions = getLanguageOptions();

  const TAB_LIST = [
    { id: 'industry', label: 'Industry' },
    { id: 'customerStories', label: 'Customer Stories' },
    { id: 'trustedPartners', label: 'Trusted partners' },
    { id: 'faq', label: 'FAQ' }
  ];

  useEffect(() => {
    dispatch(fetchCodeDictionaries({ region: REGION_SG, type: DOMAIN }));
    dispatch(fetchIndustryProducts({ language: LANGUAGE_DEFAULT_OPTION, region: REGION_SG }));

    dispatch(fetchLanguageOptions());

    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      const mnId = payload?.mn_id;

      if (mnId) {
        const menuPermissions = mnId.split('|');
        const filteredPermissions = menuPermissions.filter(perm => perm && perm.trim() !== '');
        const targetMenu = filteredPermissions.find(perm => perm.includes('020300'));

        if (targetMenu) {
          if (targetMenu.trim().endsWith('-R')) {
            setPermission('R');
          } else {
            setPermission('RWX');
          }
        } else {
          setPermission('RWX');
        }
      } else {
        setPermission('RWX');
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (industryProducts?.resdata) {
      let products = [];
      const { resdata } = industryProducts;

      if (resdata.productList?.length) products = resdata.productList;
      else if (Array.isArray(resdata)) products = resdata;
      else if (resdata.data?.length) products = resdata.data;

      const formattedProducts = products.map(item => ({
        id: item.uuid || item.id,
        uuid: item.uuid || item.id,
        name: item.name || '',
        description: item.description || '',
        domainType: item.domainType || [],
      }));

      setAvailableProducts(formattedProducts);
    }
  }, [industryProducts]);

  useEffect(() => {
    if (selectedProductType && availableProducts.length > 0) {
      const domainType = selectedProductType.value;
      const productsForDomain = availableProducts
        .filter(product => product.domainType.some(typeString =>
          typeString.split(',').map(domain => domain.trim()).includes(domainType)
        ))
        .map(product => product.uuid);

      setSelectedProducts(productsForDomain);
    }
  }, [selectedProductType, availableProducts]);

  const toggleLanguage = (event) => {
    const selectedLanguage = event.target.value;
    setLanguage(selectedLanguage);
    dispatch(fetchIndustryProducts({ language: selectedLanguage, region: REGION_SG }));
  };

  const getProductTypeOptions = useCallback(() => {
    if (!codeDictionaries?.resdata?.data) return [];
    const languageData = codeDictionaries.resdata.data[language];
    if (!languageData?.length) return [];

    const options = [];
    languageData.forEach((item) => {
      if (item.type === DOMAIN && item.codes?.length) {
        item.codes.forEach((codeItem) => {
          options.push({ value: codeItem.code, label: codeItem.value });
        });
      }
    });
    return options;
  }, [codeDictionaries, language]);

  const productTypeOptions = getProductTypeOptions();

  useEffect(() => {
    if (productTypeOptions.length > 0 && !selectedProductType) {
      setSelectedProductType(productTypeOptions[0]);
    }
  }, [productTypeOptions, selectedProductType]);

  const handleCheckboxChange = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSaveIndustry = async () => {
    if (!selectedProductType) return;

    const requestBody = {
      domainType: selectedProductType.value,
      productMasterUuids: selectedProducts,
    };

    const response = await dispatch(updateIndustryProducts({ requestBody }));
    if (response?.payload?.status === STATUS_CODE_RESPONSE_OK) {
      dispatch(fetchIndustryProducts({ language, region: REGION_SG }));
    }
  };

  const saveContent = async (ref, pageName) => {
    setSaving(true);
    try {
      const data = ref.current?.getData();
      if (data) {
        const response = await dispatch(saveIndustryContent({
          language,
          region: REGION_SG,
          type: 'homePage',
          page: pageName,
          contentData: data
        }));

        if (response?.payload?.status === STATUS_CODE_RESPONSE_OK) {
          console.log(`${pageName} saved successfully`);
        }
      }
    } catch (error) {
      console.error(`Failed to save ${pageName}:`, error);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    switch (activeTab) {
      case 'industry':
        await handleSaveIndustry();
        break;
      case 'customerStories':
        await saveContent(customerStoriesRef, 'customerStories');
        break;
      case 'trustedPartners':
        await saveContent(trustedPartnersRef, 'trustedPartners');
        break;
      case 'faq':
        await saveContent(faqRef, 'faq');
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (updateSuccess || updateError) {
      const timer = setTimeout(() => dispatch(clearUpdateStatus()), 3000);
      return () => clearTimeout(timer);
    }
  }, [updateSuccess, updateError, dispatch]);

  const AddNewButton = ({ componentRef }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '20px'
    }}>
      <button
        className="package-add-btn"
        onClick={() => {
          if (componentRef.current && componentRef.current.addGroup) {
            componentRef.current.addGroup();
          }
        }}
      >
        Add New
      </button>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'industry':
        return (
          <div className="tab-content">
            <div className="product-type-selector">
              <label htmlFor="product-type">Industry type:</label>
              <Select
                id="product-type"
                className="product-type-dropdown"
                classNamePrefix="react-select"
                value={selectedProductType}
                onChange={setSelectedProductType}
                options={productTypeOptions}
                placeholder={
                  dictionaryLoading
                    ? "Loading..."
                    : productTypeOptions.length === 0
                      ? "No options available"
                      : "Select product type..."
                }
                isSearchable={false}
                isDisabled={dictionaryLoading}
                isLoading={dictionaryLoading}
                styles={{
                  control: (base) => ({
                    ...base,
                    boxShadow: "none",
                    borderColor: "#ccc",
                    "&:hover": {
                      borderColor: "#ccc",
                    },
                  }),
                  option: (base) => ({
                    ...base,
                    backgroundColor: "transparent",
                    ":hover": {
                      backgroundColor: "#ED1A3D33",
                    },
                  }),
                }}
              />
            </div>

            <div className="tab-package-container">
              <div>
                {availableProducts.map(product => (
                  <div key={product.id} className="product-item">
                    <label className="product-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.uuid)}
                        onChange={() => handleCheckboxChange(product.uuid)}
                        className="product-checkbox-input"
                      />
                      <span className={`product-checkmark ${selectedProducts.includes(product.uuid) ? 'checked' : ''}`}>
                        {selectedProducts.includes(product.uuid) && <span className="checkmark-icon">✓</span>}
                      </span>
                      <span className="product-text">
                        <span className="product-label">{product.name}:</span>
                        {product.description && <span className="product-name">{product.description}</span>}
                      </span>
                    </label>
                  </div>
                ))}

                {availableProducts.length === 0 && !productsLoading && (
                  <div className="no-products">No products available in industry configuration</div>
                )}
              </div>
            </div>
          </div>
        );
      case 'customerStories':
        return (
          <div className="tab-content">
            <CustomerStories ref={customerStoriesRef} language={language} />
            <AddNewButton componentRef={customerStoriesRef} />
          </div>
        );
      case 'trustedPartners':
        return (
          <div className="tab-content">
            <TrustedPartners ref={trustedPartnersRef} language={language} />
            <AddNewButton componentRef={trustedPartnersRef} />
          </div>
        );
      case 'faq':
        return (
          <div className="tab-content">
            <FAQ ref={faqRef} language={language} />
            <AddNewButton componentRef={faqRef} />
          </div>
        );

      default:
        return null;
    }
  };

  const isLoading = dictionaryLoading || productsLoading || saving || updateLoading;

  if (isLoading) {
    return (
      <div className="create-user-container">
        <div className="package-wrapper">
          <div className="package-container">
            <div className="loading">{saving || updateLoading ? 'Saving...' : 'Loading Industry...'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-user-container">
      <div className="package-wrapper">

        <div>
          <select
            className="language-select"
            value={language}
            onChange={toggleLanguage}
            style={{
              padding: '1px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '60px',
              minHeight: '30px',
              outline: 'none',
            }}
            disabled={languageOptionsLoading}
          >
            {languageOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>


        <div className="package-detail-edit-container">
          <div className="tabs-navigation">
            {TAB_LIST.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {renderTabContent()}
        </div>

        <div className="button-group" style={{ marginTop: '20px' }}>
          <button
            className={`package-detail-edit-save ${updateLoading || permission === 'R' ? 'disabled' : ''}`}
            onClick={handleSave}
            disabled={updateLoading || permission === 'R'}
          >
            {updateLoading ? 'Saving...' : 'Save'}
          </button>
        </div>

      </div>
    </div>
  );
}

export default ManageIndustry;